import type { FragmentType } from 'src/gql';
import numeral from 'numeral';
import ItemDetailsRow from 'src/components/portfolio/views/item-details-row';
import { ASSET_CLASS_DISPLAY_NAMES, ASSET_SUBCLASS_DISPLAY_NAMES } from 'src/constants/account-link';
import { graphql, unmaskFragment } from 'src/gql';

import defaultGuard from '@savvy/utils/default-guard';

export const ViewAssetDialogDetailRowsFragment = graphql(/* GraphQL */ `
  fragment ViewAssetDialogDetailRows on Asset {
    description
    assetClass
    assetSubclass
    notes
    value {
      amount
    }
    companyEquityCompanyName
    companyEquityCompanyTicker
    companyEquityCurrentPrice
  }
`);

type Props = {
  readonly assetFrag: FragmentType<typeof ViewAssetDialogDetailRowsFragment>;
};
export function ViewAssetDialogDetailRows({ assetFrag }: Props) {
  const { assetClass, ...asset } = unmaskFragment(ViewAssetDialogDetailRowsFragment, assetFrag);
  const notesRow = asset.notes ? (
    <ItemDetailsRow disableTypography detailLabel="Notes" value={<NotesValue notes={asset.notes} />} />
  ) : null;

  switch (assetClass) {
    case 'company_equity':
      return (
        <div>
          <ItemDetailsRow detailLabel="Company name" value={asset.companyEquityCompanyName} />
          <ItemDetailsRow detailLabel="Company ticker" value={asset.companyEquityCompanyTicker} />
          <ItemDetailsRow
            detailLabel="Current price"
            value={numeral(asset.companyEquityCurrentPrice).format('$0,0.00')}
          />
          {notesRow}
        </div>
      );
    case 'real_estate':
      return (
        <div>
          <ItemDetailsRow detailLabel="Address" value={asset.description} />
          <ItemDetailsRow
            detailLabel="Asset type"
            value={`Real Estate - ${ASSET_SUBCLASS_DISPLAY_NAMES[asset.assetSubclass || 'other']}`}
          />
          <ItemDetailsRow detailLabel="Value" value={numeral(asset.value.amount).format('$0,0.00')} />
          {notesRow}
        </div>
      );
    case 'collectible':
    case 'insurance_policy':
    case 'other':
    case 'vehicle':
      return (
        <div>
          <ItemDetailsRow detailLabel="Name" value={asset.description} />
          <ItemDetailsRow detailLabel="Type" value={ASSET_CLASS_DISPLAY_NAMES[assetClass]} />
          <ItemDetailsRow detailLabel="Value" value={numeral(asset.value.amount).format('$0,0.00')} />
          {notesRow}
        </div>
      );
    default:
      return defaultGuard(assetClass, null);
  }
}

function NotesValue({ notes }: { readonly notes: string }) {
  return <span style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>{notes}</span>;
}
